import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';

const BrowserFrame: React.FC<{ src: string; alt: string; url?: string }> = ({
  src, alt, url = 'transferpaneli.com'
}) => (
  <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-200 dark:border-gray-700 bg-white">
    {/* Browser chrome */}
    <div className="bg-gray-100 dark:bg-gray-800 px-4 py-2.5 flex items-center gap-3 border-b border-gray-200 dark:border-gray-700">
      {/* Traffic lights */}
      <div className="flex items-center gap-1.5 shrink-0">
        <div className="w-3 h-3 rounded-full bg-red-400" />
        <div className="w-3 h-3 rounded-full bg-yellow-400" />
        <div className="w-3 h-3 rounded-full bg-green-400" />
      </div>
      {/* URL bar */}
      <div className="flex-1 bg-white dark:bg-gray-700 rounded-md px-3 py-1 flex items-center gap-2 max-w-xs mx-auto border border-gray-200 dark:border-gray-600">
        <svg className="w-3 h-3 text-gray-400 shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
        </svg>
        <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{url}</span>
      </div>
    </div>
    {/* Screenshot */}
    <img src={src} alt={alt} className="w-full block" />
  </div>
);

export const LandingPage: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState<number | null>(null);
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  if (isAuthenticated) {
    return <Navigate to="/app/dashboard" replace />;
  }

  return (
    <div className="antialiased bg-white dark:bg-gray-900">

      {/* ── NAVBAR ── */}
      <header className="fixed w-full z-50 bg-white/90 backdrop-blur border-b border-gray-100 dark:bg-gray-900/90 dark:border-gray-800">
        <nav className="px-4 lg:px-6 py-3">
          <div className="flex flex-wrap justify-between items-center mx-auto max-w-screen-xl">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-purple-700 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">TP</span>
              </div>
              <span className="text-xl font-bold text-gray-900 dark:text-white">Transfer Paneli</span>
            </Link>

            <div className="flex items-center lg:order-2 gap-2">
              <a href="mailto:turkeyairporttransfer90@gmail.com" className="hidden sm:block text-purple-700 dark:text-purple-400 border border-purple-300 dark:border-purple-700 hover:bg-purple-50 dark:hover:bg-purple-900/20 font-medium rounded-lg text-sm px-4 py-2 transition-colors">
                Demo Talep Et
              </a>
              <Link to="/login" className="text-white bg-purple-700 hover:bg-purple-800 font-medium rounded-lg text-sm px-5 py-2 transition-colors shadow-sm">
                Giriş Yap
              </Link>
              <button onClick={() => setMenuOpen(!menuOpen)} className="lg:hidden p-2 text-gray-500 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            <div className={`${menuOpen ? 'flex' : 'hidden'} justify-between items-center w-full lg:flex lg:w-auto lg:order-1`}>
              <ul className="flex flex-col mt-4 font-medium lg:flex-row lg:space-x-8 lg:mt-0">
                {[
                  { href: '#features', label: 'Özellikler' },
                  { href: '#how-it-works', label: 'Nasıl Çalışır' },
                  { href: '#pricing', label: 'Fiyatlar' },
                  { href: '#faq', label: 'SSS' },
                ].map(item => (
                  <li key={item.href}>
                    <a href={item.href} onClick={() => setMenuOpen(false)}
                      className="block py-2 pr-4 pl-3 text-gray-700 hover:text-purple-700 lg:p-0 dark:text-gray-300 dark:hover:text-white transition-colors">
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </nav>
      </header>

      {/* ── HERO ── */}
      <section className="pt-24 pb-12 lg:pt-32 lg:pb-20 bg-gradient-to-br from-white via-purple-50/30 to-white dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="max-w-screen-xl px-4 mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="inline-block bg-purple-100 text-purple-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-6 dark:bg-purple-900 dark:text-purple-300">
              Transfer Operasyon Yönetimi
            </span>
            <h1 className="text-4xl font-extrabold leading-tight tracking-tight text-gray-900 md:text-5xl xl:text-6xl dark:text-white mb-6">
              Transfer Operasyonunuzu<br />
              <span className="text-purple-700 dark:text-purple-400">Tek Panelden</span> Yönetin
            </h1>
            <p className="text-lg text-gray-500 dark:text-gray-400 mb-8 max-w-2xl mx-auto">
              Kaynak firmalardan gelen talepleri saniyeler içinde havuz şoförlerine veya anlaşmalı firmalara atayın.
              Fiyatlandırma, ödeme takibi ve kar raporu otomatik.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/login" className="inline-flex items-center justify-center px-6 py-3 text-base font-semibold text-white bg-purple-700 rounded-xl hover:bg-purple-800 transition-colors shadow-lg shadow-purple-200 dark:shadow-none">
                Ücretsiz Başla
                <svg className="w-5 h-5 ml-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </Link>
              <a href="mailto:turkeyairporttransfer90@gmail.com" className="inline-flex items-center justify-center px-6 py-3 text-base font-semibold text-gray-900 border-2 border-gray-200 rounded-xl hover:border-purple-300 hover:bg-purple-50 transition-colors dark:text-white dark:border-gray-700 dark:hover:bg-gray-800">
                Demo Talep Et
              </a>
            </div>
          </div>

          {/* Hero screenshot */}
          <div className="relative mx-auto max-w-5xl">
            <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent dark:from-gray-900 z-10 pointer-events-none rounded-2xl" style={{top: '70%'}} />
            <BrowserFrame src="/screenshots/11.png" alt="Transfer Paneli Dashboard" />
          </div>
        </div>
      </section>

      {/* ── TRUSTED BY ── */}
      <section className="bg-gray-50 dark:bg-gray-800 border-y border-gray-100 dark:border-gray-700">
        <div className="max-w-screen-xl px-4 py-8 mx-auto">
          <p className="text-center text-sm font-medium text-gray-500 dark:text-gray-400 mb-6">
            Otel, hastane ve transfer firmalarının tercih ettiği platform
          </p>
          <div className="flex flex-wrap justify-center gap-8 text-gray-400">
            {['Oteller', 'Hastaneler', 'Turizm Acenteleri', 'Transfer Firmaları', 'Kurumsal'].map((name, i) => (
              <span key={i} className="text-sm font-semibold uppercase tracking-wider">{name}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="bg-white dark:bg-gray-900">
        <div className="max-w-screen-xl px-4 py-12 mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { value: '268+', label: 'Transfer Yönetildi', icon: '🚗' },
              { value: '₺812K', label: 'İşlem Hacmi', icon: '💰' },
              { value: '7', label: 'Kullanıcı Rolü', icon: '👥' },
              { value: '7/24', label: 'Erişim & Takip', icon: '⚡' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl mb-2">{stat.icon}</div>
                <div className="text-3xl font-extrabold text-gray-900 dark:text-white">{stat.value}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section id="features" className="bg-gray-50 dark:bg-gray-800">
        <div className="max-w-screen-xl px-4 py-16 mx-auto lg:py-24 lg:px-6 space-y-24">

          {/* Feature 1: Takvim & Transfer Takibi */}
          <div className="items-center gap-12 lg:grid lg:grid-cols-2">
            <div>
              <span className="text-purple-700 font-semibold text-sm uppercase tracking-wider dark:text-purple-400">Planlama</span>
              <h2 className="mt-3 mb-4 text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white">
                Takvim Görünümü ile Tam Kontrol
              </h2>
              <p className="mb-8 text-gray-500 lg:text-lg dark:text-gray-400">
                Aylık veya haftalık takvimde tüm transferlerinizi görün. Renk kodlarıyla anlık durum takibi,
                tek tıkla detay ve düzenleme. Mobilde haftalık liste görünümü.
              </p>
              <ul className="space-y-4">
                {[
                  'Aylık ve haftalık takvim görünümü',
                  'Durum bazlı renk kodlaması (Onaylandı, Devam Ediyor, Tamamlandı...)',
                  'Günlük transfer detay modalı',
                  'Telefonda otomatik haftalık liste görünümü',
                  'Anlık durum filtresi ve arama',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-purple-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700 dark:text-gray-300 font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-8 lg:mt-0">
              <BrowserFrame src="/screenshots/22.png" alt="Takvim Görünümü" url="transferpaneli.com/app/transfers" />
            </div>
          </div>

          {/* Feature 2: Havuz + Firma */}
          <div className="items-center gap-12 lg:grid lg:grid-cols-2">
            <div className="order-2 lg:order-1 mt-8 lg:mt-0">
              <BrowserFrame src="/screenshots/33.png" alt="Tedarikçi Ödemeleri" url="transferpaneli.com/app/admin/provider-payments" />
            </div>
            <div className="order-1 lg:order-2">
              <span className="text-purple-700 font-semibold text-sm uppercase tracking-wider dark:text-purple-400">Havuz & Tedarikçi</span>
              <h2 className="mt-3 mb-4 text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white">
                Havuz + Anlaşmalı Firma Hibrit Modeli
              </h2>
              <p className="mb-8 text-gray-500 lg:text-lg dark:text-gray-400">
                Aynı transferi istediğinize göre genel havuza, özel gruba veya doğrudan anlaşmalı firmaya
                tek tıkla atayın. Tedarikçi ve havuz üyesi ödemeleri tamamen ayrı yönetilir.
              </p>
              <ul className="space-y-4">
                {[
                  'Genel havuz ve bölge/araç tipine göre özel gruplar',
                  'Bireysel şoförler transferleri havuzdan seçer',
                  'Anlaşmalı firmalar kendi sürücülerini atar',
                  'Gerçek zamanlı atama ve durum bildirimi',
                  'Tedarikçi ve havuz ödemeleri ayrı takip',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-purple-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700 dark:text-gray-300 font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Feature 3: Finansal Takip */}
          <div className="items-center gap-12 lg:grid lg:grid-cols-2">
            <div>
              <span className="text-purple-700 font-semibold text-sm uppercase tracking-wider dark:text-purple-400">Finans</span>
              <h2 className="mt-3 mb-4 text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white">
                Akıllı Fiyatlandırma & Finansal Takip
              </h2>
              <p className="mb-8 text-gray-500 lg:text-lg dark:text-gray-400">
                Rota bazlı fiyat tabloları oluşturun. Müşteri fiyatı ve tedarikçi maliyetini girin,
                net kar otomatik hesaplansın. Tüm tahsilatları ve ödemeleri tek ekrandan takip edin.
              </p>
              <ul className="space-y-4">
                {[
                  'Kaynak firma bazlı fiyat listeleri (TRY, EUR, USD, GBP)',
                  'Tedarikçi maliyet takibi, otomatik kar hesabı',
                  'Müşteri cari hesap ve tahsilat takibi',
                  'Ödeme durumu: ödendi / bekliyor',
                  'Aylık ciro, maliyet ve kar özet raporu',
                  'Excel import/export ile toplu veri yönetimi',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-purple-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700 dark:text-gray-300 font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-8 lg:mt-0">
              <BrowserFrame src="/screenshots/44.png" alt="Ödeme Takibi" url="transferpaneli.com/app/admin/customer-payments" />
            </div>
          </div>

          {/* Feature 4: Analitik & Raporlama */}
          <div className="items-center gap-12 lg:grid lg:grid-cols-2">
            <div className="order-2 lg:order-1 mt-8 lg:mt-0">
              <BrowserFrame src="/screenshots/analytics.png" alt="Analitik Dashboard" url="transferpaneli.com/app/dashboard" />
            </div>
            <div className="order-1 lg:order-2">
              <span className="text-purple-700 font-semibold text-sm uppercase tracking-wider dark:text-purple-400">Analitik</span>
              <h2 className="mt-3 mb-4 text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white">
                Gerçek Zamanlı Dashboard & Raporlar
              </h2>
              <p className="mb-8 text-gray-500 lg:text-lg dark:text-gray-400">
                Haftalık transfer trendleri, durum dağılımı, aylık gelir–maliyet–kar karşılaştırması.
                Operasyonunuzun nabzını anlık tutun.
              </p>
              <ul className="space-y-4">
                {[
                  'Günlük ve toplam operasyon özeti',
                  'Haftalık transfer trendi grafiği',
                  'Durum dağılımı (pasta grafiği)',
                  'Aylık gelir / maliyet / kar karşılaştırması',
                  'Finansal Excel raporu (tek tıkla dışa aktar)',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-purple-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700 dark:text-gray-300 font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

        </div>
      </section>

      {/* ── FEATURE GRID (küçük özellikler) ── */}
      <section className="bg-white dark:bg-gray-900">
        <div className="max-w-screen-xl px-4 py-16 mx-auto lg:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white">Her şey dahil</h2>
            <p className="mt-4 text-gray-500 dark:text-gray-400">Operasyonunuzu yönetmek için ihtiyacınız olan tüm araçlar</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: '📋', title: 'Form & Webhook Entegrasyonu', desc: 'MS Forms, Power Automate gibi sistemlerden gelen rezervasyonlar otomatik işlenir, onaylanınca transfer oluşturulur.' },
              { icon: '✉️', title: 'SMS & Email Bildirimleri', desc: 'Transfer oluşturma, atama ve durum değişimlerinde otomatik SMS (Netgsm) ve email (Resend/AWS SES) gönderimi.' },
              { icon: '📊', title: 'Excel Import/Export', desc: 'Toplu transfer yükleme, finansal rapor ve transfer listesini tek tıkla Excel\'e aktar.' },
              { icon: '🔐', title: '7 Kullanıcı Rolü', desc: 'Platform Admin, Org Admin, Yönetici, Personel, Tedarikçi, Havuzcu — granüler izin yönetimi.' },
              { icon: '📍', title: 'Google Places Entegrasyonu', desc: 'Lokasyon girişinde otomatik tamamlama. Hatalı adres girişi riski sıfır.' },
              { icon: '📱', title: 'Mobil Uyumlu', desc: 'Telefon ve tabletten tam erişim. Havuz üyeleri transferleri mobilde görüp kabul eder.' },
              { icon: '💱', title: 'Çoklu Para Birimi', desc: 'TRY, EUR, USD, GBP. Her transfer için ayrı para birimi tanımlanabilir.' },
              { icon: '🔄', title: 'Transfer Eşleştirme', desc: 'Gidiş–dönüş transferlerini eşleştirin, aynı şoföre atayın, toplam maliyeti tek görün.' },
              { icon: '📜', title: 'Değişiklik Geçmişi', desc: 'Kim, ne zaman, neyi değiştirdi — tüm işlemler audit log\'da kayıtlı.' },
            ].map((f, i) => (
              <div key={i} className="p-6 bg-gray-50 rounded-2xl border border-gray-100 hover:border-purple-200 hover:shadow-md transition-all dark:bg-gray-800 dark:border-gray-700">
                <div className="text-3xl mb-3">{f.icon}</div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{f.title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how-it-works" className="bg-gray-50 dark:bg-gray-800">
        <div className="max-w-screen-xl px-4 py-16 mx-auto lg:py-24 lg:px-6">
          <div className="max-w-2xl mx-auto mb-12 text-center">
            <h2 className="text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white">Nasıl Çalışır?</h2>
            <p className="mt-4 text-gray-500 dark:text-gray-400 sm:text-lg">Talepten ödemeye kadar kesintisiz dijital iş akışı</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: '01', emoji: '📥', title: 'Talep Gelir', desc: 'Kaynak firma (otel, hastane, kurum) sisteme transfer talebi oluşturur ya da form webhook\'u tetikler — otomatik işlenir.' },
              { step: '02', emoji: '🎯', title: 'Atama Yapılır', desc: 'Admin transferi genel havuza, özel gruba (Lüks Araç, Antalya Bölgesi) ya da doğrudan anlaşmalı firmaya tek tıkla atar.' },
              { step: '03', emoji: '🚗', title: 'Şoför Kabul Eder', desc: 'Havuz üyesi sisteme giriş yapar, grubundaki açık transferleri görür ve seçer. SMS bildirimi alır.' },
              { step: '04', emoji: '💳', title: 'Takip Et & Öde', desc: 'Transfer tamamlanır, kar otomatik hesaplanır. Müşteri tahsilatı ve tedarikçi/havuz ödemeleri ayrı ayrı kayıt altına alınır.' },
            ].map((item, i) => (
              <div key={i} className="relative">
                {i < 3 && (
                  <div className="hidden lg:block absolute top-8 left-full w-full h-0.5 bg-purple-100 dark:bg-purple-900 z-0" style={{width: 'calc(100% - 2rem)', left: 'calc(50% + 2rem)'}} />
                )}
                <div className="relative z-10 flex flex-col items-center text-center">
                  <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900 rounded-2xl flex items-center justify-center text-3xl mb-4">
                    {item.emoji}
                  </div>
                  <span className="text-xs font-bold text-purple-700 dark:text-purple-400 uppercase tracking-wider mb-2">Adım {item.step}</span>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{item.title}</h3>
                  <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIAL ── */}
      <section className="bg-white dark:bg-gray-900">
        <div className="max-w-screen-xl px-4 py-16 mx-auto text-center lg:py-20 lg:px-6">
          <div className="max-w-2xl mx-auto bg-purple-50 dark:bg-gray-800 rounded-3xl p-8 lg:p-12 border border-purple-100 dark:border-gray-700">
            <div className="text-5xl mb-6">"</div>
            <blockquote className="text-xl font-medium text-gray-900 dark:text-white md:text-2xl leading-relaxed">
              Günlük 30+ transferi WhatsApp ve kağıtla yönetiyorduk, her şey birbirine giriyordu.
              Transfer Paneli'ne geçtikten sonra tüm operasyon netleşti. Atama, takip, ödeme — hepsi tek yerde.
            </blockquote>
            <div className="flex items-center justify-center mt-8 gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-200 dark:bg-purple-800 flex items-center justify-center">
                <span className="text-purple-700 dark:text-purple-300 font-bold text-sm">AK</span>
              </div>
              <div className="text-left">
                <div className="font-semibold text-gray-900 dark:text-white">Ahmet K.</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Operasyon Müdürü — Antalya</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" className="bg-gray-50 dark:bg-gray-800">
        <div className="max-w-screen-xl px-4 py-16 mx-auto lg:py-24 lg:px-6">
          <div className="max-w-2xl mx-auto mb-12 text-center">
            <h2 className="text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white">
              Operasyon büyüklüğünüze göre
            </h2>
            <p className="mt-4 text-gray-500 dark:text-gray-400 sm:text-lg">
              Küçük ekiplerden büyük kurumsal operasyonlara kadar esnek fiyatlandırma
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                name: 'Başlangıç',
                price: '₺0',
                period: 'Şu an ücretsiz',
                desc: 'Küçük ekipler ve bireysel operatörler için',
                highlighted: false,
                features: ['Sınırsız transfer kaydı', 'Havuz ve grup yönetimi', 'Tedarikçi & sürücü takibi', 'Excel export', 'Temel raporlar', '5 kullanıcıya kadar'],
                cta: 'Ücretsiz Başla',
                ctaHref: '/login',
                isLink: true,
              },
              {
                name: 'Kurumsal',
                price: 'Özel Teklif',
                period: 'Operasyon büyüklüğüne göre',
                desc: 'Büyük ekipler ve çok şubeli operasyonlar',
                highlighted: true,
                features: ['Her şey dahil', 'Sınırsız kullanıcı', 'SMS & Email bildirimleri', 'Webhook entegrasyonu', 'Öncelikli teknik destek', 'Özel eğitim & onboarding'],
                cta: 'Demo Talep Et',
                ctaHref: 'mailto:turkeyairporttransfer90@gmail.com',
                isLink: false,
              },
              {
                name: 'API & Entegrasyon',
                price: 'İletişime Geç',
                period: 'Mevcut sisteme entegre',
                desc: 'Mevcut yazılımınıza entegre etmek isteyenler',
                highlighted: false,
                features: ['REST API erişimi', 'Webhook desteği', 'Özel form entegrasyonu', 'SLA garantisi', 'Teknik destek', 'Özel geliştirme'],
                cta: 'İletişime Geç',
                ctaHref: 'mailto:turkeyairporttransfer90@gmail.com',
                isLink: false,
              },
            ].map((plan, i) => (
              <div key={i} className={`flex flex-col p-8 rounded-2xl border-2 ${
                plan.highlighted
                  ? 'bg-purple-700 border-purple-700 text-white shadow-2xl shadow-purple-200 dark:shadow-none scale-105'
                  : 'bg-white border-gray-100 dark:bg-gray-900 dark:border-gray-700'
              }`}>
                <h3 className={`text-2xl font-bold mb-2 ${plan.highlighted ? 'text-white' : 'text-gray-900 dark:text-white'}`}>{plan.name}</h3>
                <p className={`text-sm mb-6 ${plan.highlighted ? 'text-purple-200' : 'text-gray-500 dark:text-gray-400'}`}>{plan.desc}</p>
                <div className="mb-1">
                  <span className={`text-4xl font-extrabold ${plan.highlighted ? 'text-white' : 'text-gray-900 dark:text-white'}`}>{plan.price}</span>
                </div>
                <p className={`text-sm mb-8 ${plan.highlighted ? 'text-purple-200' : 'text-gray-400'}`}>{plan.period}</p>
                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.map((f, fi) => (
                    <li key={fi} className="flex items-center gap-3">
                      <svg className={`w-5 h-5 shrink-0 ${plan.highlighted ? 'text-purple-200' : 'text-purple-600'}`} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className={plan.highlighted ? 'text-purple-50' : 'text-gray-700 dark:text-gray-300'}>{f}</span>
                    </li>
                  ))}
                </ul>
                {plan.isLink ? (
                  <Link to={plan.ctaHref} className={`text-center font-semibold rounded-xl py-3 px-6 transition-colors ${
                    plan.highlighted ? 'bg-white text-purple-700 hover:bg-purple-50' : 'bg-purple-700 text-white hover:bg-purple-800'
                  }`}>{plan.cta}</Link>
                ) : (
                  <a href={plan.ctaHref} className={`text-center font-semibold rounded-xl py-3 px-6 transition-colors ${
                    plan.highlighted ? 'bg-white text-purple-700 hover:bg-purple-50' : 'bg-purple-700 text-white hover:bg-purple-800'
                  }`}>{plan.cta}</a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="bg-white dark:bg-gray-900">
        <div className="max-w-screen-xl px-4 py-16 mx-auto lg:py-24 lg:px-6">
          <h2 className="text-3xl font-extrabold text-center text-gray-900 dark:text-white mb-12">Sık Sorulan Sorular</h2>
          <div className="max-w-3xl mx-auto divide-y divide-gray-200 dark:divide-gray-700">
            {[
              { q: 'Havuz sistemi ile anlaşmalı firma sistemi aynı anda kullanılabilir mi?', a: 'Evet. Her transfer için bağımsız karar verebilirsiniz: bu transferi genel havuza at, şunu lüks araç grubuna, şunu doğrudan X firmasına. İkisi birlikte çalışır.' },
              { q: 'MS Forms veya harici formlardan gelen rezervasyonlar nasıl aktarılır?', a: 'Power Automate üzerinden webhook entegrasyonu kurulur. Form doldurulduğunda sistem otomatik alır, yönetici onayladığında transfer otomatik oluşturulur.' },
              { q: 'SMS ve email bildirimleri nasıl çalışır?', a: 'Transfer oluşturma, atama ve durum değişimlerinde otomatik SMS (Netgsm) ve email gönderilir. Her organizasyon kendi SMS ve email hesabını bağlayabilir.' },
              { q: 'Excel import nasıl çalışır?', a: 'Mevcut transfer verilerinizi belirli bir formatta Excel ile sisteme toplu yükleyebilirsiniz. Dışa aktarımda da tüm transferler filtrelenmiş şekilde Excel\'e çıkar.' },
              { q: 'Kaynak firma (müşteri kurum) bakiyesi nasıl takip edilir?', a: 'Her kaynak firma için ayrı bakiye tutulur. Tahsilat yapıldıkça sisteme işlenir, kalan bakiye anlık görülür. Cari hesap ve transfer başına ödeme modları desteklenir.' },
              { q: 'Mobil kullanım var mı?', a: 'Evet, tam responsive tasarım. Havuz üyeleri telefondan giriş yapıp transferleri kabul edebilir. Takvim haftalık liste görünümüyle telefonda rahatça okunur.' },
            ].map((item, i) => (
              <div key={i}>
                <button onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                  className="flex items-center justify-between w-full py-5 text-left font-semibold text-gray-900 dark:text-white hover:text-purple-700 dark:hover:text-purple-400 transition-colors">
                  <span>{item.q}</span>
                  <svg className={`w-5 h-5 shrink-0 ml-4 transition-transform text-purple-600 ${faqOpen === i ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
                {faqOpen === i && (
                  <div className="pb-5 text-gray-500 dark:text-gray-400 leading-relaxed">{item.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section className="bg-purple-700 dark:bg-purple-800">
        <div className="max-w-screen-xl px-4 py-16 mx-auto text-center lg:py-20 lg:px-6">
          <h2 className="mb-4 text-3xl font-extrabold text-white">
            Transfer Operasyonunuzu Dijitalleştirin
          </h2>
          <p className="mb-8 text-purple-100 md:text-lg max-w-xl mx-auto">
            Kağıt kalem ve WhatsApp mesajlarından kurtulun. Ücretsiz başlayın, istediğiniz zaman büyütün.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/login" className="inline-flex items-center justify-center px-6 py-3 text-base font-semibold bg-white text-purple-700 rounded-xl hover:bg-purple-50 transition-colors">
              Ücretsiz Başla — Kredi kartı gerekmez
            </Link>
            <a href="mailto:turkeyairporttransfer90@gmail.com" className="inline-flex items-center justify-center px-6 py-3 text-base font-semibold border-2 border-white text-white rounded-xl hover:bg-purple-600 transition-colors">
              Demo Talep Et
            </a>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-gray-900 text-gray-400">
        <div className="max-w-screen-xl p-4 py-12 mx-auto lg:py-16 md:p-8 lg:p-10">
          <div className="grid grid-cols-2 gap-8 md:grid-cols-4 lg:grid-cols-5">
            <div className="col-span-2">
              <Link to="/" className="flex items-center gap-2.5 mb-4">
                <div className="w-8 h-8 bg-purple-700 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">TP</span>
                </div>
                <span className="text-xl font-bold text-white">Transfer Paneli</span>
              </Link>
              <p className="text-sm leading-relaxed">
                Havalimanı ve otel transfer operasyonları için komple yönetim platformu.
              </p>
            </div>
            <div>
              <h2 className="mb-4 text-sm font-semibold text-white uppercase tracking-wider">Ürün</h2>
              <ul className="space-y-3 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Özellikler</a></li>
                <li><a href="#how-it-works" className="hover:text-white transition-colors">Nasıl Çalışır</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Fiyatlar</a></li>
                <li><a href="#faq" className="hover:text-white transition-colors">SSS</a></li>
              </ul>
            </div>
            <div>
              <h2 className="mb-4 text-sm font-semibold text-white uppercase tracking-wider">Şirket</h2>
              <ul className="space-y-3 text-sm">
                <li><a href="mailto:turkeyairporttransfer90@gmail.com" className="hover:text-white transition-colors">İletişim</a></li>
                <li><Link to="/login" className="hover:text-white transition-colors">Giriş Yap</Link></li>
              </ul>
            </div>
            <div>
              <h2 className="mb-4 text-sm font-semibold text-white uppercase tracking-wider">Yasal</h2>
              <ul className="space-y-3 text-sm">
                <li><Link to="/privacy" className="hover:text-white transition-colors">Gizlilik Politikası</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Kullanım Şartları</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-10 pt-8 border-t border-gray-800 text-center text-sm">
            © {new Date().getFullYear()} Transfer Paneli. Tüm hakları saklıdır.
          </div>
        </div>
      </footer>

    </div>
  );
};
